import { useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { strategiesApi } from "@/lib/api";
import { reportsApi, type ReportExecutionMode } from "@/lib/reportsApi";
import { useRememberedExecutionMode } from "@/hooks/useRememberedExecutionMode";
import { istDayOf, monthKeyOf, monthRange, tradingDays } from "./exitStats";
import {
  EMPTY_FILTERS,
  ReportFilterBar,
  applyFilters,
  type ReportFilterState,
} from "./ReportFilters";
import { TradesMiniTable } from "./ComparisonTable";
import { MonthChips, ReportModeToggle, SummaryGrid } from "./reportWidgets";
import { AnalyticsSection } from "./AnalyticsSection";

/** One strategy's own report page — /reports/:strategyId. Month tabs +
 * the exit-style cards + the full trade-by-trade comparison table.
 * Every strategy (bias today; supertrend etc. later) automatically gets
 * its own page at its own URL, mirroring the reference dashboard's
 * per-strategy detail pages. */
type ReportTab = "analytics" | "comparison";

export function StrategyReportPage(): ReactElement {
  const { strategyId } = useParams<{ strategyId: string }>();
  const [tab, setTab] = useState<ReportTab>("analytics");
  const [month, setMonth] = useState<string>(() => monthKeyOf(new Date()));
  const [mode, setMode] = useRememberedExecutionMode();
  const strategies = useQuery({
    queryKey: ["strategies"],
    queryFn: () => strategiesApi.list(),
  });
  const strategy = strategies.data?.find((s) => s.id === strategyId);

  return (
    <main className="mx-auto max-w-6xl px-6 py-6">
      <Link
        to="/reports"
        className="mb-3 inline-flex items-center gap-1 text-sm text-ink-secondary hover:text-ink"
      >
        <i className="ti ti-arrow-left text-sm" /> All reports
      </Link>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="mb-1">
            <i className="ti ti-report-analytics mr-1 text-accent" />
            {strategy?.name ?? "…"} — report
          </h1>
          <p className="text-sm text-ink-secondary">
            Performance analytics and exit-style comparison (LIVE vs Fixed vs Pivot).
          </p>
        </div>
        <ReportModeToggle value={mode} onChange={setMode} />
      </header>
      <ReportTabs value={tab} onChange={setTab} />
      {tab === "analytics" && strategyId !== undefined && (
        <AnalyticsSection strategyId={strategyId} mode={mode} />
      )}
      {tab === "comparison" && (
        <>
          <MonthChips active={month} onPick={setMonth} strategyId={strategyId} mode={mode} />
          {strategies.data !== undefined && strategy === undefined && (
            <p className="text-sm text-danger">Strategy not found.</p>
          )}
          {strategy !== undefined && (
            <ReportBody strategyId={strategy.id} month={month} mode={mode} />
          )}
        </>
      )}
    </main>
  );
}

function ReportTabs({
  value,
  onChange,
}: Readonly<{ value: ReportTab; onChange: (t: ReportTab) => void }>): ReactElement {
  const tabs: readonly { key: ReportTab; label: string; icon: string }[] = [
    { key: "analytics", label: "Analytics", icon: "ti-chart-histogram" },
    { key: "comparison", label: "Exit comparison", icon: "ti-columns" },
  ];
  return (
    <div className="mb-4 flex gap-1 border-b border-line" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={value === t.key}
          className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${
            value === t.key
              ? "border-accent text-accent"
              : "border-transparent text-ink-secondary hover:text-ink"
          }`}
          onClick={() => {
            onChange(t.key);
          }}
        >
          <i className={`ti ${t.icon} text-base`} />
          {t.label}
        </button>
      ))}
    </div>
  );
}

function ReportBody({
  strategyId,
  month,
  mode,
}: Readonly<{ strategyId: string; month: string; mode: ReportExecutionMode }>): ReactElement {
  const { from, to } = monthRange(month);
  const [filters, setFilters] = useState<ReportFilterState>(EMPTY_FILTERS);
  const report = useQuery({
    queryKey: ["exit-comparison", strategyId, month, mode],
    queryFn: () => reportsApi.exitComparison(from, to, strategyId, mode),
  });
  if (report.isPending) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (report.isError) {
    return <p className="text-sm text-danger">Failed to load this month&apos;s report.</p>;
  }
  if (report.data.trades.length === 0) {
    return <p className="py-4 text-sm text-ink-secondary">No trades this month.</p>;
  }
  const days = [...new Set(report.data.trades.map((t) => istDayOf(t.report_at)))].sort((a, b) =>
    a.localeCompare(b),
  );
  const filtered = applyFilters(report.data.trades, report.data.vix, filters);
  return (
    <>
      <ReportFilterBar filters={filters} days={days} onChange={setFilters} />
      <div className="mb-3">
        <span className="pill bg-surface-2 text-ink-secondary">
          {String(filtered.length)} of {String(report.data.trades.length)} trades ·{" "}
          {String(tradingDays(filtered))} day(s)
        </span>
      </div>
      {filtered.length === 0 ? (
        <p className="py-4 text-sm text-ink-secondary">No trades match the filters.</p>
      ) : (
        <>
          <SummaryGrid trades={filtered} strategyId={strategyId} />
          <TradesMiniTable trades={filtered} vix={report.data.vix} />
        </>
      )}
    </>
  );
}
